import { configureStore } from '@reduxjs/toolkit';
import axios from 'axios';
import logActionsMiddleware from '../middleware/logActions';
import quotaReducer from './features/quotaReducer';
import { quotaExceeded } from './features/quotaActions';
import sessionReducer from '../session/sessionReducer';
import sessionMiddleware from '../middleware/session';
import loginReducer from '../pages/login/loginReducer';
import seeInspectionReducer from '../pages/inspections/see-inspection/seeInspectionReducer';
import { baseApi } from '../services/api/baseApi';
import isDev from '../utils/isDev';
import {
  REFRESH_SESSION,
  REFRESH_SESSION_FAILED,
  SET_SESSION,
  CLEAR_SESSION,
} from '../session/types';

let middleware = [sessionMiddleware];

if (isDev()) {
  middleware = [logActionsMiddleware, ...middleware];
}

const store = configureStore({
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        // Cognito session payloads (REFRESH_SESSION*) carry non-serializable
        // wrapper objects. The session/SET_SESSION payload also contains the
        // user object pulled from /auth/me. Ignored here so redux-toolkit's
        // dev check doesn't flag them on every login.
        ignoredActions: [
          REFRESH_SESSION,
          REFRESH_SESSION_FAILED,
          SET_SESSION,
          CLEAR_SESSION,
        ],
      },
    })
      .concat(baseApi.middleware)
      .concat(middleware),
  reducer: {
    session: sessionReducer,
    user: loginReducer,
    seeInspection: seeInspectionReducer,
    quota: quotaReducer,
    [baseApi.reducerPath]: baseApi.reducer,
  },
});

// Inject auth headers on every axios call. RTK Query has its own
// prepareHeaders, but pages that still hit the API via raw axios
// (credits, see-inspection PDF, login bootstrap) used to rely on
// axios.defaults that the retired apiMiddleware maintained — without
// this interceptor they 401 once apiMiddleware is gone.
axios.interceptors.request.use((cfg) => {
  const session = store.getState().session?.current;
  if (session?.token) {
    cfg.headers = cfg.headers || {};
    if (!cfg.headers.Authorization) {
      cfg.headers.Authorization = `Bearer ${session.token}`;
    }
    if (session.tenant && !cfg.headers['X-Tenant-Id']) {
      cfg.headers['X-Tenant-Id'] = session.tenant;
    }
  }
  return cfg;
});

// Mirror the RTK Query 402 handler for raw axios calls (e.g. PDF
// generation in see-inspection). Without this, quota-exceeded errors
// from legacy thunks are silently swallowed and the upgrade dialog
// never opens.
axios.interceptors.response.use(undefined, (error) => {
  if (error.response?.status === 402) {
    const payload = error.response.data?.error;
    if (payload && payload.code === 'QUOTA_EXCEEDED') {
      store.dispatch(quotaExceeded(payload));
    }
  }
  return Promise.reject(error);
});

export default store;
